import { GooglePay } from '@df/module-google-pay/google-pay.namespace';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { ApplePay } from '@df/module-apple-pay/apple-pay.namespace';

declare module '@df/checkout/checkout.interfaces' {
    namespace Checkout {
        interface ISubmissionResponseResult {
            adyen?: Adyen.IPayment;
        }
    }
}

declare global {
    interface Window {
        AdyenCheckout?(configuration: Adyen.IConfiguration): void;
    }
}

export namespace Adyen {
    export enum Endpoints {
        Payment = 'adyen/payment',
        ThreeDS2Process = 'adyen/threeDS2Process',
        CancelThreeDS2 = 'adyen/threeDSCancel',
        ApplePaySession = 'adyen/applepaysession'
    }

    export enum LoadingContext {
        LIVE = 'https://checkoutshopper-live.adyen.com/checkoutshopper/',
        TEST = 'https://checkoutshopper-test.adyen.com/checkoutshopper/'
    }

    export enum Assets {
        JS = 'sdk/3.0.0/adyen.js',
        CSS = 'sdk/3.0.0/adyen.css'
    }

    export enum PaymentMethod {
        ApplePay = 'adyen_apple_pay',
        CreditCard = 'adyen_cc',
        Oneclick = 'adyen_oneclick',
        GooglePay = 'adyen_google_pay'
    }

    export enum Component {
        Card = 'card',
        ApplePay = 'applepay',
        ThreeDS2DeviceFingerprint = 'threeDS2DeviceFingerprint',
        ThreeDS2Challenge = 'threeDS2Challenge',
        GooglePay = 'paywithgoogle'
    }

    export enum ThreeDS2Type {
        IdentifyShopper = 'IdentifyShopper',
        ChallengeShopper = 'ChallengeShopper'
    }

    export const cancel3DSResolveName = 'cancel3DS';

    export const currencyConverterMap = {
        IDR: 1,
        JPY: 1,
        KRW: 1,
        VND: 1,
        BYR: 1,
        CVE: 1,
        DJF: 1,
        GHC: 1,
        GNF: 1,
        KMF: 1,
        PYG: 1,
        RWF: 1,
        UGX: 1,
        VUV: 1,
        XAF: 1,
        XOF: 1,
        XPF: 1,
        MRO: 10,
        BHD: 1e3,
        JOD: 1e3,
        KWD: 1e3,
        OMR: 1e3,
        LYD: 1e3,
        TND: 1e3
    };

    export interface IBrowserInfo {
        screen_width: number | '';
        screen_height: number | '';
        screen_color_depth: number | '';
        java_enabled: boolean;
        timezone_offset: number;
        language: string;
    }

    ////////////////////////////////////////////
    ////////
    ////////    Adyen payment methods
    ////////
    ////////////////////////////////////////////

    export interface IPaymentMethod extends Checkout.IPaymentMethod {
        key: string;
        isTest?: boolean;
    }

    export interface ICardPaymentMethod extends IPaymentMethod {
        isVault?: boolean;
        // availble card types
        availableTypes?: string[];
    }

    export interface ICcCreditCardPaymentMethod extends IPaymentMethod {}

    export interface IApplePayPaymentMethod extends IPaymentMethod {
        merchantIdentifier: string;
    }

    export interface IGooglePayPaymentMethod extends IPaymentMethod {
        checkoutEnvironment?: string;
        format?: number;
        locale?: string;
        merchantAccount?: string;
        merchantIdentifier?: string;
    }

    ////////////////////////////////////////////
    ////////
    ////////    Adyen checkout
    ////////
    ////////////////////////////////////////////

    export interface ICheckout {
        options: IConfiguration;
        create: (componentType: Component | string, options: IComponentOptions) => IComponent;
    }

    export interface IConfiguration {
        locale: string;
        originKey: string;
        loadingContext: string;
    }

    ////////////////////////////////////////////
    ////////
    ////////    Adyen components
    ////////
    ////////////////////////////////////////////

    export interface IComponent {
        mount: (...args) => void;
        unmount: (...args) => void;
        remount: (...args) => void;
        onValid: (...args) => void;
        setState: (...args) => void;
        render: (...args) => void;
        isValid: boolean;
        componentRef?: any;
        data?: any;
    }

    export interface ICreditCardComponent extends IComponent {
        showValidation: () => void;
        state: ICardComponentState;
        onChange: IComponentOnChange;
        props: ICardComponentOptions;
    }

    export interface IApplePayComponent extends IComponent {
        isAvailable(): Promise<void>;
    }

    ////////////////////////////////////////////
    ////////
    ////////    Adyen components options
    ////////
    ////////////////////////////////////////////

    export interface IComponentOptions {
        onChange?: IComponentOnChange;
    }

    export type IComponentOnChange = (state: ICardComponentState, component: any) => void;

    export interface ICardComponentOptions extends IComponentOptions {
        enableStoreDetails?: boolean;
        details?: ICardComponentDetails[];
        hasHolderName?: boolean;
        holderNameRequired?: boolean;
    }

    export interface ICardComponentDetails {
        key: string;
        type?: string;
    }

    export interface IOneclickComponentOptions extends ICardComponentOptions {
        type: string;
        hideCVC: boolean;
        storedDetails: {
            card: IStoredDetails;
        };
    }

    export interface IStoredDetails {
        expiryMonth: number;
        expiryYear: number;
        number: string | number;
        holderName?: string;
    }

    export interface IThreeDS2ComponentOptions extends IComponentOptions {
        onComplete?: IThreeDS2ComponentOnComplete;
        onError?: (error) => void;
    }

    export type IThreeDS2ComponentOnComplete = (result?: IThreeDS2ComponentResult) => void;

    export interface IThreeDS2ComponentResult {
        data: IThreeDS2ComponentResultData;
    }

    export interface IThreeDS2ComponentResultData {
        paymentData?: string;
        details: IThreeDS2ComponentResultDataDetails;
    }

    export interface IThreeDS2ComponentResultDataDetails {
        'threeds2.fingerprint': string;
        'threeds2.challengeResult': string;
    }

    export type IThreeDS2OnProcessSuccess = (processResponse: IPayment) => void;

    export interface IDeviceFingerprintComponentOptions extends IThreeDS2ComponentOptions {
        fingerprintToken?: string;
    }

    export interface IChallengeComponentOptions extends IThreeDS2ComponentOptions {
        challengeToken?: string;
    }

    ////////////////////////////////////////////
    ////////
    ////////    Adyen applepay component
    ////////
    ////////////////////////////////////////////

    export interface IApplePayComponentOptions extends IComponentOptions {
        currencyCode: string;
        amount: number;
        countryCode: string;
        configuration: IApplePayConfiguration;
        version?: number;
        totalPriceStatus?: string;
        totalPriceLabel?: string;
        showButton?: boolean;
        buttonType?: 'plain' | 'buy' | 'donate' | 'check-out' | 'book' | 'subscribe';
        buttonColor?: 'black' | 'white' | 'white-with-line';
        onError?(): void;
        onValidateMerchant(resolve, reject, validationURL: string): void;
        onAuthorized?(resolve, reject, event: ApplePay.IPaymentEvent): void;
    }

    export interface IApplePayConfiguration {
        merchantName?: string;
        merchantIdentifier: string;
    }

    ////////////////////////////////////////////
    ////////
    ////////    Adyen googlepay component
    ////////
    ////////////////////////////////////////////

    export interface IGooglePayComponent extends IComponent {
        isAvailable(): Promise<void>;
    }

    export interface IGooglePayComponentOptions extends IComponentOptions {
        environment: string;
        configuration?: IGooglePayComponentConfigurationOptions;
        amount: number;
        currency: string;
        totalPriceStatus: string;
        onError?(): void;
        onAuthorized?(event: GooglePay.IPaymentData): void;
        buttonColor?: 'default' | 'black' | 'white';
        buttonType?: 'long' | 'short';
        showButton?: boolean;
        allowedAuthMethods?: string[];
        allowedCardNetworks?: string;
        existingPaymentMethodRequired?: boolean;
        allowPrepaidCards?: boolean;
        billingAddressRequired?: boolean;
        billingAddressParameters?: { [key: string]: any };
        emailRequired?: boolean;
        shippingAddressRequired?: boolean;
        shippingAddressParameters?: { [key: string]: any };
    }

    export interface IGooglePayComponentConfigurationOptions {
        gatewayMerchantId?: string;
        merchantIdentifier?: string;
        merchantName?: string;
    }

    ////////////////////////////////////////////
    ////////
    ////////    Adyen credit card component
    ////////
    ////////////////////////////////////////////

    export interface ICardComponentState {
        data?: ICardComponentData;
        isValid?: boolean;
        brand?: string; // card type
        cvcRequired?: boolean;
        holderNameRequired?: boolean;
        hasHolderName?: boolean;
        hasStoreDetails?: boolean;
        hideCVCForBrand?: boolean;
        isCardValid?: boolean;
        storePaymentMethod?: boolean;
        status?: string;
        valid?: {
            encryptedCardNumber?: boolean;
            encryptedExpiryMonth?: boolean;
            encryptedExpiryYear?: boolean;
            encryptedSecurityCode?: boolean;
        };
    }

    export interface ICardComponentData {
        encryptedCardNumber?: string;
        encryptedExpiryMonth?: string;
        encryptedExpiryYear?: string;
        encryptedSecurityCode?: string;
        holderName?: string;
    }

    export interface IPayment {
        threeDS2?: boolean;
        token?: string;
        type?: string;
        result?: 'Authorised';
        error?: string;
    }

    export interface ICcData {
        method: any;
        additional_data: ICcAdditionalData;
    }

    export interface ICcAdditionalData {
        cc_type?: string;
        number?: string;
        cvc?: string;
        expiryMonth?: string;
        expiryYear?: string;
        holderName?: string;
        store_cc?: boolean;
        number_of_installments?: number | '';
        java_enabled: boolean;
        screen_color_depth?: number | '';
        screen_width?: number | '';
        screen_height?: number | '';
        timezone_offset: number;
        language?: string;
    }

    ////////////////////////////////////////////
    ////////
    ////////    Adyen credit card component submission data
    ////////
    ////////////////////////////////////////////

    export interface IPaymentSubmissionData<T = ICardSubmissionData | IOneClickSubmissionData> {
        method: PaymentMethod;
        additional_data: T;
    }

    export interface ICardSubmissionData {
        cc_type?: string;
        store_cc?: boolean;
        number: string;
        cvc: string;
        expiryMonth: string;
        expiryYear: string;
        holderName?: string;
    }

    export interface IOneClickSubmissionData {
        cardId: number;
        cvc?: string;
    }
}